import { RotateCcw, Wand2 } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { defaultRobot } from '../../hooks';
import type { RobotValues } from '../../types';

interface Props {
  comp: Competition;
  robot: RobotValues;
  setRobot: (v: RobotValues) => void;
}

export default function BuildTab({ comp, robot, setRobot }: Props) {
  const update = (key: string, value: number | string) => setRobot({ ...robot, [key]: value });
  const applyPreset = (values: Record<string, number | string>) => setRobot({ ...robot, ...values });
  const resetDefaults = () => setRobot(defaultRobot(comp));

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Robot configuration</h3>
          <button
            type="button"
            onClick={resetDefaults}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-300 ring-1 ring-white/10 transition hover:text-white hover:ring-white/25"
          >
            <RotateCcw className="size-3.5" />
            Reset to defaults
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {comp.configFields.map((f) => (
            <div key={f.key} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor={f.key} className="text-sm font-semibold text-slate-200">
                  {f.label}
                </label>
                <span className={`text-sm font-bold tabular-nums ${comp.text}`}>
                  {robot[f.key]}
                  {f.unit ? ` ${f.unit}` : ''}
                </span>
              </div>
              {f.type === 'range' ? (
                <input
                  id={f.key}
                  type="range"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={Number(robot[f.key])}
                  onChange={(e) => update(f.key, Number(e.target.value))}
                  className="mt-3 w-full accent-amber-400"
                />
              ) : (
                <select
                  id={f.key}
                  value={String(robot[f.key])}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="mt-3 w-full rounded-lg border border-white/10 bg-navy-950/60 px-2.5 py-1.5 text-sm font-medium text-slate-100 outline-none focus:border-cyan-400/40"
                >
                  {f.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
              {f.help && <p className="mt-2 text-xs leading-relaxed text-slate-500">{f.help}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          <Wand2 className="size-4" />
          Presets
        </h3>
        <p className="text-sm text-slate-400">
          Start from a known build, then tune individual values and re-run the simulation.
        </p>
        <div className="space-y-2">
          {comp.presets.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => applyPreset(preset.values)}
              className="block w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left text-sm font-bold text-slate-200 transition hover:border-white/20 hover:bg-white/[0.06]"
            >
              {preset.name}
              <span className="mt-0.5 block text-xs font-normal text-slate-500">
                {Object.entries(preset.values)
                  .slice(0, 3)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
